import React, { useState, useMemo, useEffect } from "react";
import { Info, ChevronDown, ChevronRight, Briefcase, Wrench, Users, AlertTriangle, ExternalLink, Search, ArrowUp, ArrowDown, ArrowUpDown, Table2, LayoutList, Pencil, Trash2 } from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { StatusBadge, DisciplineBadge } from "../components/Badges";
import { JobDetailModal } from "../components/JobDetailModal";
import { LineActionPicker, StatusTimeline, LineNotes, CalPeriodTag } from "../components/job-detail/JobLinesTable";
import { EditLineModal } from "../components/EditLineModal";
import { RemoveLineModal } from "../components/RemoveLineModal";
import { useLineActionRunner } from "../components/job-detail/useLineActionRunner";
import { useFetch } from "../lib/useApi";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtNum } from "../lib/format";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

// Statuses eligible for the bulk "Mark Calibrated" action. Covers items
// still awaiting a bench outcome (WORK_OUTCOME_OK) AND items that already
// have one outcome recorded (calibrated/adjusted/repaired) — SPT-PTE-2026-0034:
// a technician can select a mixed group (e.g. one Repaired, one Adjusted, one
// Calibrated) and bulk-override them all to Calibrated in one action before
// sending to admin, mirroring the always-available per-line "Mark Calibrated"
// option in job-detail/JobLinesTable.js's lineActionOptions() (simplifyAfterCalibration mode).
const BULK_CALIBRATE_ELIGIBLE = new Set([
  "received", "assigned", "in_progress", "parts_required", "awaiting_spares",
  "parts_received", "awaiting_customer_approval", "awaiting_quote",
  "returned_from_subcontractor", "failed_calibration", "unable_to_calibrate",
  "calibrated", "adjusted", "repaired",
]);
// Statuses eligible for the bulk "Send to Admin" action (bench outcome
// already recorded — nothing left to capture before handing off).
const BULK_ADMIN_ELIGIBLE = new Set(["calibrated", "adjusted", "repaired"]);

// Statuses that are still the technician's responsibility (in PTE's lab, not yet
// handed to admin / dispatched). Items leave this list the moment they are sent
// to admin or beyond.
// SPT-PTE-2026-0037: "awaiting_spares" is intentionally EXCLUDED — once a tool
// is waiting on spares/parts, it's out of the technician's hands (Parts/Manager
// need to order/receive it) and should only surface in the Awaiting Spares
// queue (/app/parts) and Active Lab, not clutter My Worklist as an unactionable
// item. "parts_required" (an earlier, still-technician-owned flag) is unaffected.
const TECH_VISIBLE = new Set([
  "received", "assigned", "in_progress",
  "parts_required", "parts_received",
  "awaiting_customer_approval", "awaiting_quote", "returned_from_subcontractor",
  "calibrated", "adjusted", "repaired",
  "failed_calibration", "unable_to_calibrate", "returned_without_work",
]);

const TABS = [
  { key: "all", label: "All", match: () => true },
  { key: "assigned", label: "Assigned", match: (s) => ["received", "assigned"].includes(s) },
  { key: "in_progress", label: "In progress", match: (s) => s === "in_progress" },
  { key: "on_hold", label: "On hold", match: (s) => ["parts_required", "parts_received", "awaiting_customer_approval", "awaiting_quote", "returned_from_subcontractor"].includes(s) },
  { key: "bench_done", label: "Lab done", match: (s) => ["calibrated", "adjusted", "repaired", "failed_calibration", "unable_to_calibrate", "returned_without_work"].includes(s) },
];

function SortTh({ label, col, sortBy, sortDir, onSort, tp = "wl-sort", ...rest }) {
  const active = sortBy === col;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th {...rest}>
      <button className="th-sort-btn" onClick={() => onSort(col)} data-testid={`${tp}-${col}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: 0,
          cursor: "pointer", font: "inherit", color: active ? "var(--text-1)" : "inherit", padding: 0 }}>
        {label} <Icon size={11} style={{ opacity: active ? 1 : 0.4 }} />
      </button>
    </th>
  );
}

// Flat, one-row-per-equipment-item view — sortable by clicking any column
// header. No Status column (techs don't need it here, they filter by the
// tabs above instead); Serial # and Issue # are always both visible.
function EquipmentView({ rows, onOpen, sortBy, sortDir, onSort }) {
  return (
    <div className="card" style={{ overflow: "hidden" }} data-testid="wl-equipment-table">
      <table className="dt">
        <thead>
          <tr>
            <SortTh label="D/N" col="delivery_note_number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Customer" col="customer_name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Equipment" col="description" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Serial #" col="serial_number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Issue #" col="issue_number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="Discipline" col="discipline" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <th>Cal Period</th>
            <SortTh label="Date received" col="date_received" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <SortTh label="CR #" col="cr" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it) => (
            <tr key={it.id} className="link-row" style={{ cursor: "pointer" }}
              onClick={() => onOpen(it.job_id)} data-testid={`wl-eq-row-${it.id}`}>
              <td className="mono t-strong" style={{ whiteSpace: "nowrap" }}>
                {["urgent", "high"].includes(it.priority) && <span className="prio-dot prio-urgent" style={{ marginRight: 6 }} title="Urgent" />}
                {it.delivery_note_number || "(no DN)"}
              </td>
              <td>{it.customer_name || "—"}</td>
              <td>{it.description || "—"}<LineNotes remarks={it.remarks} notes={it.notes} testId={`wl-eq-notes-${it.id}`} /></td>
              <td className="mono" data-testid={`wl-eq-serial-${it.id}`}>{it.serial_number || "—"}</td>
              <td className="mono" data-testid={`wl-eq-issue-${it.id}`}>{it.issue_number || "—"}</td>
              <td>{it.discipline ? <DisciplineBadge name={it.discipline} /> : "—"}</td>
              <td><CalPeriodTag days={it.calibration_period_days} testId={`wl-eq-calperiod-${it.id}`} /></td>
              <td className="mono t-mut">{fmtDate(it.date_received)}</td>
              <td className="mono t-mut">{it.certificate_number || it.reserved_certificate_number || "—"}</td>
              <td style={{ textAlign: "right" }}>
                <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); onOpen(it.job_id); }}
                  data-testid={`wl-eq-open-${it.id}`}>
                  <ExternalLink size={12} /> Open job
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Expanded drill-down: the job's allocated equipment lines, independently
// sortable on Serial #, Issue #, Description, Discipline, Status and CR #.
// Each line also gets an inline "Select action… → Go" picker so a
// technician can action a specific item without opening the full job.
function JobDrillTable({ jobId, items, onRunAction, busy, canEdit, isTechnician, isManager, onEditLine, onRemoveLine, tabMatch, refetch }) {
  const [sortBy, setSortBy] = useState("serial_number");
  const [sortDir, setSortDir] = useState("asc");
  const [openTimeline, setOpenTimeline] = useState(null);
  const [q, setQ] = useState("");
  // SPT-PTE-2026-0009 — multi-select checkboxes so a technician can close
  // several same-type items (e.g. probes/gauges calibrated simultaneously)
  // in a single bulk action instead of one at a time.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // "Solo" focus: once a technician actions a line (Repair/Adjust/Calibrate/
  // etc.), keep it pinned at the top — regardless of whether its NEW status
  // still matches the active Worklist tab — so they can chain the next step
  // (e.g. Calibrate -> Send to Admin) on the SAME item without it jumping
  // back down into "the rest of the list". Clears once they Send to Admin
  // (the line has genuinely left their bench work at that point).
  const [pinnedId, setPinnedId] = useState(null);
  const handleRunAction = (action, item) => {
    setPinnedId(action === "send_to_admin" ? null : item.id);
    onRunAction(action, item);
  };
  const onSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };
  const { sorted, matchCount } = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (it) => (sortBy === "cr" ? (it.certificate_number || it.reserved_certificate_number) : it[sortBy]) || "";
    const cmp = (a, b) => String(val(a)).localeCompare(String(val(b)), undefined, { numeric: true }) * dir;
    const pinned = pinnedId ? items.find((it) => it.id === pinnedId) : null;
    const rest0 = pinned ? items.filter((it) => it.id !== pinnedId) : items;
    if (!tabMatch) {
      const s = [...rest0].sort(cmp);
      return { sorted: pinned ? [pinned, ...s] : s, matchCount: s.length + (pinned ? 1 : 0) };
    }
    // Surface the lines matching the currently-selected tab at the top —
    // the rest of the job's lines (other status bands) stay visible
    // underneath, just grouped out of the way since they aren't the focus
    // of this tab.
    const match = rest0.filter((it) => tabMatch(it.status)).sort(cmp);
    const rest = rest0.filter((it) => !tabMatch(it.status)).sort(cmp);
    const sorted = pinned ? [pinned, ...match, ...rest] : [...match, ...rest];
    return { sorted, matchCount: (pinned ? 1 : 0) + match.length };
  }, [items, sortBy, sortDir, tabMatch, pinnedId]);
  // Quick "find my tool" search — a technician working a big job (e.g. 60+
  // lines) can type the Serial # or Issue # they're holding and jump
  // straight to it instead of scanning/sorting the whole list.
  const qq = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!qq) return sorted;
    return sorted.filter((it) =>
      (it.serial_number || "").toLowerCase().includes(qq) ||
      (it.issue_number || "").toLowerCase().includes(qq));
  }, [sorted, qq]);
  const th = { sortBy, sortDir, onSort, tp: `wl-drill-sort-${jobId}` };
  const toggleSelAll = () => setSelected((s) => (s.size === filtered.length ? new Set() : new Set(filtered.map((it) => it.id))));
  const selectedItems = items.filter((it) => selected.has(it.id));
  const canBulkCalibrate = selectedItems.length > 0 && selectedItems.every((it) => BULK_CALIBRATE_ELIGIBLE.has(it.status));
  const canBulkAdmin = selectedItems.length > 0 && selectedItems.every((it) => BULK_ADMIN_ELIGIBLE.has(it.status));
  const runBulk = async (status, label) => {
    setBulkBusy(true);
    try {
      const r = await api.post("/job-items/bulk-status", { item_ids: [...selected], status });
      toast.success(`${r.data.count} item${r.data.count !== 1 ? "s" : ""} → ${label}`);
      if (r.data.skipped?.length) toast.message(`${r.data.skipped.length} skipped`);
      setSelected(new Set());
      refetch && refetch();
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid={`wl-drill-bulk-bar-${jobId}`}>
          <span className="t-mut" style={{ fontSize: 12 }}>{selected.size} selected</span>
          <button className="btn sm gold" disabled={!canBulkCalibrate || bulkBusy}
            title="Mark all selected items as Calibrated, even if they currently have different outcomes (Adjusted/Repaired/Calibrated)"
            onClick={() => runBulk("calibrated", "Calibrated")}
            data-testid={`wl-drill-bulk-calibrate-${jobId}`}>
            Mark Calibrated
          </button>
          <button className="btn sm ghost" disabled={!canBulkAdmin || bulkBusy}
            title="Select two or more calibrated/adjusted/repaired items to enable"
            onClick={() => runBulk("ready_for_admin", "Sent to Admin")}
            data-testid={`wl-drill-bulk-admin-${jobId}`}>
            Send to Admin
          </button>
          {selected.size > 0 && (
            <button className="btn sm ghost" onClick={() => setSelected(new Set())}
              data-testid={`wl-drill-bulk-clear-${jobId}`}>
              Clear
            </button>
          )}
        </div>
        <div className="search" style={{ maxWidth: 230 }}>
          <Search size={13} />
          <input placeholder="Find by S/N or I/N…" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid={`wl-drill-search-${jobId}`} />
        </div>
      </div>
      {qq && filtered.length === 0 && (
        <div className="t-mut" style={{ fontSize: 12.5, padding: "8px 4px" }} data-testid={`wl-drill-search-empty-${jobId}`}>
          No line on this job matches "{q}".
        </div>
      )}
    <table className="dt" style={{ background: "var(--card)" }} data-testid={`wl-job-drill-${jobId}`}>
      <thead>
        <tr>
          <th style={{ width: 26 }}>
            <input type="checkbox" data-testid={`wl-drill-select-all-${jobId}`}
              checked={selected.size === filtered.length && filtered.length > 0}
              onChange={toggleSelAll} />
          </th>
          <th style={{ width: 28 }}></th>
          <SortTh label="Serial #" col="serial_number" {...th} />
          <SortTh label="Issue #" col="issue_number" {...th} />
          <SortTh label="Description" col="description" {...th} />
          <SortTh label="Part #" col="part_number" {...th} />
          <SortTh label="Discipline" col="discipline" {...th} />
          <th>Cal Period</th>
          <SortTh label="Job Ref / CR #" col="cr" {...th} />
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((it, idx) => {
          const isOpen = openTimeline === it.id;
          const showDivider = !qq && tabMatch && idx === matchCount && matchCount > 0 && matchCount < filtered.length;
          return (
          <React.Fragment key={it.id}>
            {showDivider && (
              <tr data-testid={`wl-drill-divider-${jobId}`}>
                <td colSpan={10} style={{ padding: "6px 14px", background: "var(--card-2)", borderTop: "1px dashed var(--line)", borderBottom: "1px dashed var(--line)" }}>
                  <span className="t-mut" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".03em" }}>
                    Other lines on this job (different status)
                  </span>
                </td>
              </tr>
            )}
            <tr data-testid={`wl-drill-row-${it.id}`} style={it.id === pinnedId ? { background: "#eef6ff" } : undefined}>
              <td>
                <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSel(it.id)}
                  data-testid={`wl-drill-select-${it.id}`} />
              </td>
              <td>
                <button className="icon-btn" style={{ width: 22, height: 22 }}
                  onClick={() => setOpenTimeline(isOpen ? null : it.id)}
                  title="Status timeline" data-testid={`wl-drill-timeline-toggle-${it.id}`}>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              </td>
              <td className="mono" data-testid={`wl-drill-serial-${it.id}`}>{it.serial_number || "—"}</td>
              <td className="mono" data-testid={`wl-drill-issue-${it.id}`}>{it.issue_number || "—"}</td>
              <td>{it.description || "—"}<LineNotes remarks={it.remarks} notes={it.notes} testId={`wl-drill-notes-${it.id}`} />
                {it.id === pinnedId && (
                  <span className="t-mut" style={{ marginLeft: 6, fontSize: 10.5, fontStyle: "italic" }} data-testid={`wl-drill-pinned-${it.id}`}>
                    · working on this
                  </span>
                )}
              </td>
              <td className="mono" data-testid={`wl-drill-part-${it.id}`}>{it.part_number || "—"}</td>
              <td>{it.discipline ? <DisciplineBadge name={it.discipline} /> : "—"}</td>
              <td><CalPeriodTag days={it.calibration_period_days} testId={`wl-drill-calperiod-${it.id}`} /></td>
              <td className="mono t-mut">{it.certificate_number || it.reserved_certificate_number || "—"}</td>
              <td>
                <div className="line-action-cell">
                  {!isTechnician && canEdit && (
                    <button className="icon-btn" title="Edit line" onClick={() => onEditLine(it)}
                      data-testid={`wl-drill-edit-${it.id}`}><Pencil size={12} /></button>
                  )}
                  {isManager && (
                    <button className="icon-btn" title="Remove line" onClick={() => onRemoveLine(it)}
                      data-testid={`wl-drill-remove-${it.id}`}><Trash2 size={12} /></button>
                  )}
                  <LineActionPicker item={it} onRun={handleRunAction} busy={busy} simplifyAfterCalibration />
                </div>
              </td>
            </tr>
            {isOpen && (
              <tr data-testid={`wl-drill-timeline-${it.id}`}>
                <td></td>
                <td></td>
                <td colSpan={8} style={{ background: "var(--card-2)", padding: "10px 14px" }}>
                  <StatusTimeline history={it.status_history || []} />
                </td>
              </tr>
            )}
          </React.Fragment>
          );
        })}
      </tbody>
    </table>
    </>
  );
}

function JobRow({ dn, group, onOpen, expanded, onToggle, onRunAction, busy, canEdit, isTechnician, isManager, onEditLine, onRemoveLine, tabMatch, refetch }) {
  const items = group.items;
  const disciplines = [...new Set(items.map((i) => i.discipline).filter(Boolean))];
  const statusCounts = items.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});
  const oldest = items.reduce((min, i) => (!min || (i.date_received && i.date_received < min) ? i.date_received : min), null);
  const urgent = items.some((i) => ["urgent", "high"].includes(i.priority));
  // The CR/certificate number is reserved for every line the moment the job
  // is received — technicians should see it from the start, not just once
  // the certificate is issued at the end of the bench work.
  const crs = [...new Set(items.map((i) => i.certificate_number || i.reserved_certificate_number).filter(Boolean))];
  const crLabel = crs.length === 0 ? "—" : crs.length === 1 ? crs[0] : `${crs[0]}–${crs[crs.length - 1]}`;
  return (
    <>
      <tr data-testid={`wl-job-${group.job_id}`} className="link-row"
        style={{ cursor: "pointer" }} onClick={() => onToggle(group.job_id)}>
        <td style={{ width: 30 }} data-testid={`wl-job-toggle-${group.job_id}`}>
          {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </td>
        <td className="mono t-strong" style={{ whiteSpace: "nowrap" }}>
          {urgent && <span className="prio-dot prio-urgent" style={{ marginRight: 6 }} title="Urgent" />}
          {dn}
        </td>
        <td className="mono t-mut" data-testid={`wl-job-cr-${group.job_id}`} style={{ whiteSpace: "nowrap" }}>{crLabel}</td>
        <td><span className="t-mut">{items.length} item{items.length !== 1 ? "s" : ""}</span></td>
        <td>
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {disciplines.slice(0, 3).map((d) => <DisciplineBadge key={d} name={d} />)}
          </span>
        </td>
        <td>
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {Object.entries(statusCounts).map(([s, n]) => (
              <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                <StatusBadge status={s} />{n > 1 && <span className="t-mut" style={{ fontSize: 11 }}>×{n}</span>}
              </span>
            ))}
          </span>
        </td>
        <td className="mono t-mut">{fmtDate(oldest)}</td>
        <td style={{ textAlign: "right" }}>
          <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); onOpen(group.job_id); }}
            data-testid={`wl-open-${group.job_id}`}>
            <ExternalLink size={12} /> Open job
          </button>
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`wl-job-drill-row-${group.job_id}`}>
          <td colSpan={8} style={{ padding: 0, background: "var(--card-2)" }}>
            <div style={{ padding: "10px 14px 14px 38px" }}>
              <JobDrillTable jobId={group.job_id} items={items} onRunAction={onRunAction} busy={busy}
                canEdit={canEdit} isTechnician={isTechnician} isManager={isManager}
                onEditLine={onEditLine} onRemoveLine={onRemoveLine} tabMatch={tabMatch} refetch={refetch} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function Worklist() {
  const { user, can } = useAuth();
  const isTechnician = user?.role === "technician";
  const isManager = user?.role === "manager" || user?.role === "admin";
  const canEdit = can("equipment", "edit");
  // SPT-PTE-2026-0062: filter by the technician-visible status list SERVER-SIDE
  // (status_in) rather than fetching an unfiltered page then filtering client-side —
  // as a technician's total job_items grows past the page size, an unfiltered
  // updated_at-sorted page can fill up entirely with their own older
  // closed/dispatched items, silently crowding out the genuinely active ones.
  const { data, loading, refetch } = useFetch("/job-items", {
    technician_id: user?.id, status_in: [...TECH_VISIBLE].join(","), limit: 5000,
  });
  const [openJobId, setOpenJobId] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [disciplineFilter, setDisciplineFilter] = useState(() => localStorage.getItem("worklist.disciplineFilter") || "");
  const [disciplines, setDisciplines] = useState([]);
  const [expandedCustomers, setExpandedCustomers] = useState({}); // customer -> bool (default: collapsed)
  const [expandedJobs, setExpandedJobs] = useState({}); // job_id -> bool
  const toggleJob = (jid) => setExpandedJobs((s) => ({ ...s, [jid]: !s[jid] }));
  const [view, setView] = useState(() => localStorage.getItem("worklist.view.v2") || "jobs");
  const [sortBy, setSortBy] = useState("delivery_note_number");
  const [sortDir, setSortDir] = useState("asc");
  const { runLineAction, busy: actionBusy, modals: actionModals } = useLineActionRunner(refetch);
  const [editingLine, setEditingLine] = useState(null);
  const [removingLine, setRemovingLine] = useState(null);

  useEffect(() => { localStorage.setItem("worklist.disciplineFilter", disciplineFilter); }, [disciplineFilter]);
  useEffect(() => { localStorage.setItem("worklist.view.v2", view); }, [view]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines((r.data || []).filter((d) => d.active !== false))).catch(() => {});
  }, []);

  const items = useMemo(() => (data?.items || []).filter((it) => TECH_VISIBLE.has(it.status)), [data]);
  // Discipline filter applies at the top scope so tab counts + stat cards also
  // reflect it (e.g. picking "Torque" should show "3 jobs" for Torque only,
  // not the technician's whole queue).
  const scopedItems = useMemo(() => (
    disciplineFilter ? items.filter((it) => it.discipline === disciplineFilter) : items
  ), [items, disciplineFilter]);

  const tabDef = TABS.find((t) => t.key === tab) || TABS[0];
  const tabCounts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.key] = scopedItems.filter((i) => t.match(i.status)).length; });
    return c;
  }, [scopedItems]);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scopedItems
      .filter((it) => tabDef.match(it.status))
      .filter((it) => !q || [it.customer_name, it.delivery_note_number, it.description, it.serial_number, it.issue_number]
        .some((x) => String(x || "").toLowerCase().includes(q)));
  }, [scopedItems, tabDef, search]);

  const grouped = useMemo(() => {
    const byCustomer = {};
    visibleItems.forEach((it) => {
      const cust = it.customer_name || "—";
      const dn = it.delivery_note_number || "(no DN)";
      byCustomer[cust] = byCustomer[cust] || { customer_number: it.customer_number, jobs: {} };
      byCustomer[cust].jobs[dn] = byCustomer[cust].jobs[dn] || { job_id: it.job_id, items: [] };
      byCustomer[cust].jobs[dn].items.push(it);
    });
    return byCustomer;
  }, [visibleItems]);

  // Full per-job grouping for the "By Job" view specifically: a job is kept
  // if it has at least one item matching the current tab + search, but the
  // drill-down gets the job's COMPLETE item list (every status) so lines
  // from a different status band aren't invisible — JobDrillTable sorts the
  // tab-relevant ones to the top and groups the rest underneath instead.
  const jobGroupsFull = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchesSearch = (it) => !q || [it.customer_name, it.delivery_note_number, it.description, it.serial_number, it.issue_number]
      .some((x) => String(x || "").toLowerCase().includes(q));
    const byCustomer = {};
    scopedItems.filter(matchesSearch).forEach((it) => {
      const cust = it.customer_name || "—";
      const dn = it.delivery_note_number || "(no DN)";
      byCustomer[cust] = byCustomer[cust] || { customer_number: it.customer_number, jobs: {} };
      byCustomer[cust].jobs[dn] = byCustomer[cust].jobs[dn] || { job_id: it.job_id, items: [] };
      byCustomer[cust].jobs[dn].items.push(it);
    });
    const result = {};
    Object.entries(byCustomer).forEach(([cust, cg]) => {
      const jobs = {};
      Object.entries(cg.jobs).forEach(([dn, jg]) => {
        if (jg.items.some((it) => tabDef.match(it.status))) jobs[dn] = jg;
      });
      if (Object.keys(jobs).length) result[cust] = { customer_number: cg.customer_number, jobs };
    });
    return result;
  }, [scopedItems, tabDef, search]);

  const flatRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (it) => (sortBy === "cr" ? (it.certificate_number || it.reserved_certificate_number) : it[sortBy]) || "";
    return [...visibleItems].sort((a, b) => String(val(a)).localeCompare(String(val(b)), undefined, { numeric: true }) * dir);
  }, [visibleItems, sortBy, sortDir]);

  const customerNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
  const totalJobs = customerNames.reduce((n, c) => n + Object.keys(grouped[c].jobs).length, 0);
  const shownItems = customerNames.reduce((n, c) => n + Object.values(grouped[c].jobs).reduce((m, j) => m + j.items.length, 0), 0);
  const urgentCount = scopedItems.filter((i) => ["urgent", "high"].includes(i.priority)).length;

  const Stat = ({ icon: Icon, label, value, tone, testid }) => (
    <div className="card pad stat" data-testid={testid}>
      <div className="lab"><span className={`ico ${tone}`}><Icon size={16} /></span>{label}</div>
      <div className="num">{fmtNum(value)}</div>
    </div>
  );

  return (
    <>
      <PageHead crumb="My Lab" title="My Worklist"
        sub="Your items grouped by customer → job → equipment. Open a job to see its equipment lines, pick an action and capture the readings — just like the Active Lab." />

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat icon={Users} label="Customers" value={customerNames.length} tone="b-blue" testid="wl-stat-customers" />
        <Stat icon={Briefcase} label="Jobs" value={totalJobs} tone="b-violet" testid="wl-stat-jobs" />
        <Stat icon={Wrench} label="Equipment items" value={shownItems} tone="b-green" testid="wl-stat-items" />
        <Stat icon={AlertTriangle} label="Urgent / High" value={urgentCount} tone="b-amber" testid="wl-stat-urgent" />
      </div>

      <div className="ftabs" style={{ marginBottom: 14, alignItems: "center" }}>
        {TABS.map((t) => (
          <button key={t.key} className={`ftab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)} data-testid={`wl-tab-${t.key}`}>
            {t.label} <span className="c">{fmtNum(tabCounts[t.key] ?? 0)}</span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <select className="input" style={{ maxWidth: 190 }} value={disciplineFilter}
          onChange={(e) => setDisciplineFilter(e.target.value)} data-testid="wl-discipline-filter">
          <option value="">All disciplines</option>
          {disciplines.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
        </select>
        <div className="search" style={{ maxWidth: 320 }}>
          <Search size={15} />
          <input value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="wl-search"
            placeholder="Search customer, DN, equipment, serial…" />
        </div>
        <div style={{ display: "inline-flex", gap: 4, padding: 3, background: "var(--card-2)", borderRadius: 8 }}>
          <button className={`ftab ${view === "equipment" ? "active" : ""}`}
            onClick={() => setView("equipment")} data-testid="wl-view-equipment" title="One row per equipment item — sortable">
            <Table2 size={13} /> Equipment
          </button>
          <button className={`ftab ${view === "jobs" ? "active" : ""}`}
            onClick={() => setView("jobs")} data-testid="wl-view-jobs" title="Grouped by Customer → Job">
            <LayoutList size={13} /> By Job
          </button>
        </div>
      </div>

      {loading && !data && <Loader />}
      {!!data && visibleItems.length === 0 && (
        <div className="card pad" style={{ textAlign: "center", color: "var(--text-3)", padding: 40 }} data-testid="wl-empty">
          No items in this section.
        </div>
      )}

      {!!data && visibleItems.length > 0 && view === "equipment" && (
        <EquipmentView rows={flatRows} onOpen={setOpenJobId} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
      )}

      {!!data && visibleItems.length > 0 && view === "jobs" && (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {Object.keys(jobGroupsFull).sort((a, b) => a.localeCompare(b)).map((cust) => {
          const cg = jobGroupsFull[cust];
          const isExpanded = !!expandedCustomers[cust];
          const jobNames = Object.keys(cg.jobs).sort();
          const custItemCount = jobNames.reduce((n, dn) => n + cg.jobs[dn].items.length, 0);
          return (
            <div className="card" key={cust} data-testid={`wl-customer-${cust}`}
              style={{
                overflow: "hidden",
                transition: "border-color .15s ease, box-shadow .15s ease",
                // SPT-PTE-2026-0010: a visibly thicker border wraps the whole
                // customer group (header + its indented job/item rows) while
                // expanded, so consecutive expanded customers read as clearly
                // separate blocks. Collapsed customers keep the plain .card look.
                ...(isExpanded ? {
                  border: "2px solid var(--gold)",
                  boxShadow: "0 6px 20px -8px rgba(150,118,43,.35)",
                } : {}),
              }}>
              <button className="wl-cust-head"
                onClick={() => setExpandedCustomers((s) => ({ ...s, [cust]: !s[cust] }))}
                data-testid={`wl-customer-toggle-${cust}`}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 14px",
                  border: 0, background: "var(--card-2, #faf9f6)", cursor: "pointer", textAlign: "left",
                  borderBottom: isExpanded ? "1px solid var(--line)" : "none" }}>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                {cg.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", fontSize: 12 }}>{cg.customer_number}</span>}
                <span className="t-strong" style={{ fontSize: 14 }}>{cust}</span>
                <span className="t-mut" style={{ fontSize: 12 }}>· {jobNames.length} job{jobNames.length !== 1 ? "s" : ""} · {custItemCount} item{custItemCount !== 1 ? "s" : ""}</span>
              </button>
              {isExpanded && (
                <table className="dt">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}></th>
                      <th>Job / DN</th><th>Job Ref / CR #</th><th>Items</th><th>Disciplines</th><th>Status</th><th>Oldest received</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobNames.map((dn) => (
                      <JobRow key={dn} dn={dn} group={cg.jobs[dn]} onOpen={setOpenJobId}
                        expanded={!!expandedJobs[cg.jobs[dn].job_id]} onToggle={toggleJob}
                        onRunAction={runLineAction} busy={actionBusy}
                        canEdit={canEdit} isTechnician={isTechnician} isManager={isManager}
                        onEditLine={setEditingLine} onRemoveLine={setRemovingLine}
                        tabMatch={tab === "all" ? null : tabDef.match} refetch={refetch} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
      )}

      <div className="notice" style={{ marginTop: 18 }}>
        <Info size={16} />
        <span>
          Expand a job to action equipment lines directly (Select action… → <b>Go</b>) without opening the full job,
          or click <b>Open job</b> for the full view. The pop-up captures the readings (Excel-style grid). Once done,
          choose <b>Send to Admin</b> to hand it off, or <b>Send back to In Progress</b> for rework.
        </span>
      </div>

      {openJobId && (
        <JobDetailModal jobId={openJobId}
          onClose={() => setOpenJobId(null)}
          onChanged={refetch} />
      )}
      {actionModals}
      {editingLine && (
        <EditLineModal item={editingLine} onClose={() => setEditingLine(null)}
          onSaved={() => { setEditingLine(null); refetch(); }} />
      )}
      {removingLine && (
        <RemoveLineModal item={removingLine} onClose={() => setRemovingLine(null)}
          onRemoved={() => { setRemovingLine(null); refetch(); }} />
      )}
    </>
  );
}
